import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Dismiss24Regular, Open16Regular } from '@fluentui/react-icons'
import type { HelpSection } from '../shell.types'
import type { ShellTelemetry } from '../telemetry/shellTelemetry'

const useStyles = makeStyles({
  drawer: {
    width: '488px',
    maxWidth: '100vw',
  },
  header: {
    padding: '36px 32px 20px',
  },
  headerTitle: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightRegular,
  },
  body: {
    padding: '8px 32px 24px',
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  sectionAfterFirst: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalXXL,
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    margin: 0,
  },
  links: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  link: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: tokens.spacingHorizontalXS,
    width: 'fit-content',
    fontSize: tokens.fontSizeBase400,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  externalIcon: {
    fontSize: tokens.fontSizeBase300,
  },
  callout: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    marginTop: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
  },
  calloutTitle: {
    color: tokens.colorNeutralForeground1,
    display: 'block',
    fontSize: tokens.fontSizeBase400,
    marginBottom: tokens.spacingVerticalS,
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalXXL,
  },
  footerLink: {
    fontSize: tokens.fontSizeBase300,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  footerDivider: {
    color: tokens.colorNeutralForeground3,
  },
})

interface HelpPanelProps {
  open: boolean
  sections: HelpSection[]
  telemetry: ShellTelemetry
  onOpenChange: (open: boolean) => void
}

export const HelpPanel = ({
  open,
  sections,
  telemetry,
  onOpenChange,
}: HelpPanelProps) => {
  const styles = useStyles()

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      position="end"
      type="overlay"
      onOpenChange={(_, data) => onOpenChange(data.open)}
    >
      <DrawerHeader className={styles.header}>
        <DrawerHeaderTitle
          className={styles.headerTitle}
          action={
            <Button
              appearance="subtle"
              aria-label="Close help"
              icon={<Dismiss24Regular />}
              onClick={() => onOpenChange(false)}
            />
          }
        >
          Help
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        <div className={styles.layout}>
          <div className={styles.sections}>
            {sections.map((section, sectionIndex) => (
              <section
                className={`${styles.section} ${
                  sectionIndex > 0 ? styles.sectionAfterFirst : ''
                }`}
                key={section.remoteName}
              >
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                <div className={styles.links}>
                  {section.links.map((link, linkIndex) => (
                    <Link
                      className={styles.link}
                      href={link.href}
                      key={`${section.remoteName}-${link.label}-${linkIndex}`}
                      rel="noopener noreferrer"
                      target="_blank"
                      onClick={() =>
                        telemetry.event('help.link.click', {
                          remoteName: section.remoteName,
                          label: link.label,
                        })
                      }
                    >
                      {link.label}
                      <Open16Regular
                        aria-hidden="true"
                        className={styles.externalIcon}
                      />
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
          <div className={styles.callout}>
            <strong className={styles.calloutTitle}>Need additional help?</strong>
            Our FrontDesk team (
            <Link
              href="https://aka.ms/FrontDesk"
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => telemetry.event('help.frontdesk.click')}
            >
              aka.ms/FrontDesk
            </Link>
            ) is ready to assist with user support, architectural consultation,
            application development, and PowerApps ownership transfers.
          </div>
          <footer className={styles.footer}>
            <Link
              className={styles.footerLink}
              href="https://www.microsoft.com/en-us/legal/terms-of-use"
              rel="noopener noreferrer"
              target="_blank"
            >
              Legal
            </Link>
            <span aria-hidden="true" className={styles.footerDivider}>
              |
            </span>
            <Link
              className={styles.footerLink}
              href="https://www.microsoft.com/en-US/privacy/data-privacy-notice"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy
            </Link>
          </footer>
        </div>
      </DrawerBody>
    </Drawer>
  )
}
